/**
 * Gallery Module
 *
 * Public view of all claimed parcels. No auth required to browse.
 * Fetches claimed parcels from Supabase and renders a grid.
 */
import { getClaimedParcels, type Parcel } from "./db";
import { store, actions, selectors } from "./store";
import { getMapInstance } from "./index";
import { renderParcelThumbnailCached } from "./thumbnailRenderer";

const PAGE_SIZE = 20;
let currentOffset = 0;
let hasMore = true;
let isLoading = false;

export function initGallery(): void {
  // Fetch gallery when view becomes active
  store.select(selectors.currentView, (view) => {
    if (view === "gallery") {
      // Only load if we haven't loaded yet
      if (store.getState().galleryParcels.length === 0) {
        loadGallery(true);
      }
    }
  });

  // Re-render when gallery parcels change
  store.select(selectors.galleryParcels, () => {
    if (store.getState().currentView === "gallery") {
      renderGallery();
    }
  });
}

async function loadGallery(reset = false): Promise<void> {
  if (isLoading) return;

  if (reset) {
    currentOffset = 0;
    hasMore = true;
    actions.setGalleryParcels([]);
  }

  isLoading = true;
  showLoading(true);

  try {
    const parcels = await getClaimedParcels({
      offset: currentOffset,
      limit: PAGE_SIZE,
      orderBy: "claimed_at",
    });

    if (parcels.length < PAGE_SIZE) {
      hasMore = false;
    }

    if (reset) {
      actions.setGalleryParcels(parcels);
    } else {
      const existing = store.getState().galleryParcels;
      actions.setGalleryParcels([...existing, ...parcels]);
    }

    currentOffset += parcels.length;
  } catch (error) {
    console.error("[Gallery] Error loading parcels:", error);
    const grid = document.getElementById("galleryGrid");
    if (grid && currentOffset === 0) {
      grid.innerHTML = `<p class="secondary">Failed to load gallery. Please try again.</p>`;
    }
  } finally {
    isLoading = false;
    showLoading(false);
  }
}

function renderGallery(): void {
  const grid = document.getElementById("galleryGrid");
  const empty = document.getElementById("galleryEmpty");

  if (!grid) return;

  const parcels = store.getState().galleryParcels;

  if (parcels.length === 0 && !isLoading) {
    if (empty) empty.classList.remove("hidden");
    grid.innerHTML = "";
    return;
  }

  if (empty) empty.classList.add("hidden");

  grid.innerHTML = parcels.map((p) => renderGalleryCard(p)).join("");

  // Add load more button if there are more
  if (hasMore) {
    grid.insertAdjacentHTML(
      "afterend",
      `<div id="galleryLoadMore" class="gallery-load-more">
        <button id="loadMoreBtn" class="header-btn">Load More</button>
      </div>`
    );

    // Remove any existing load more buttons first
    const existingLoadMore = document.querySelectorAll("#galleryLoadMore");
    existingLoadMore.forEach((el, i) => {
      if (i > 0) el.remove(); // Keep only the last one
    });

    document.getElementById("loadMoreBtn")?.addEventListener("click", () => {
      loadGallery(false);
    });
  } else {
    const loadMore = document.getElementById("galleryLoadMore");
    if (loadMore) loadMore.remove();
  }

  // Render thumbnails
  renderGalleryThumbnails(parcels);

  // Add click handlers
  grid.querySelectorAll("[data-gallery-parcel]").forEach((card) => {
    card.addEventListener("click", () => {
      const id = (card as HTMLElement).dataset.galleryParcel;
      const parcel = parcels.find((p) => p.id === id);
      if (parcel) navigateToGalleryParcel(parcel);
    });
  });
}

function renderGalleryCard(parcel: Parcel): string {
  const claimedDate = parcel.claimedAt
    ? new Date(parcel.claimedAt).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : "";
  const parcelName = parcel.name || "Untitled";

  return `
    <div class="parcel-card gallery-card" data-gallery-parcel="${parcel.id}">
      <div class="parcel-thumbnail">
        <div class="thumbnail-loading" data-gallery-thumb="${parcel.id}">
          <span class="thumbnail-spinner"></span>
        </div>
      </div>
      <div class="gallery-card-info">
        <span class="gallery-owner">${escapeHtml(parcelName)}</span>
        <span class="gallery-date secondary">${claimedDate}</span>
      </div>
    </div>
  `;
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

async function renderGalleryThumbnails(parcels: Parcel[]): Promise<void> {
  const promises = parcels.map(async (parcel) => {
    const container = document.querySelector(
      `[data-gallery-thumb="${parcel.id}"]`
    );
    if (!container) return;

    // Try Storage URL first, fall back to WASM rendering
    if (parcel.thumbnailUrl) {
      try {
        await loadImage(parcel.thumbnailUrl);
        container.innerHTML = `<img src="${parcel.thumbnailUrl}" alt="Fractal thumbnail" crossorigin="anonymous" />`;
        container.classList.remove("thumbnail-loading");
        container.classList.add("thumbnail-loaded");
        return;
      } catch {
        console.warn(`[Gallery] Storage thumbnail failed for ${parcel.id}, falling back to WASM`);
      }
    }

    try {
      const dataUrl = await renderParcelThumbnailCached(parcel, 256);
      container.innerHTML = `<img src="${dataUrl}" alt="Fractal thumbnail" />`;
      container.classList.remove("thumbnail-loading");
      container.classList.add("thumbnail-loaded");
    } catch (error) {
      console.error(`[Gallery] Thumbnail render failed for ${parcel.id}:`, error);
      container.innerHTML = `<span class="thumbnail-error">!</span>`;
      container.classList.remove("thumbnail-loading");
    }
  });

  await Promise.all(promises);
}

/** Preload an image URL, rejects if it fails */
function loadImage(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve();
    img.onerror = () => reject();
    img.src = url;
  });
}

function navigateToGalleryParcel(parcel: Parcel): void {
  const map = getMapInstance();
  if (!map) return;

  const url = new URL(window.location.href);

  url.searchParams.set("re", parcel.re.toString());
  url.searchParams.set("im", parcel.im.toString());
  url.searchParams.set("z", parcel.zoom.toString());
  url.searchParams.set("i", parcel.iterations.toString());
  url.searchParams.set("c", parcel.colorScheme);
  url.searchParams.set("r", parcel.reverseColors.toString());
  url.searchParams.set("sc", parcel.smoothColoring.toString());
  url.searchParams.set("pmin", parcel.paletteMinIter.toString());
  url.searchParams.set("pmax", parcel.paletteMaxIter.toString());
  url.searchParams.set("swi", parcel.scaleWithIterations.toString());
  url.searchParams.set("h", parcel.shiftHueAmount.toString());
  url.searchParams.set("s", parcel.saturateAmount.toString());
  url.searchParams.set("l", parcel.lightenAmount.toString());
  url.searchParams.set("cs", parcel.colorSpace.toString());

  window.history.replaceState({}, "", url.toString());
  map.setConfigFromUrl();

  actions.setMapPosition({
    re: parcel.re,
    im: parcel.im,
    zoom: parcel.zoom,
  });

  actions.navigateTo("explorer");
}

function showLoading(show: boolean): void {
  const loading = document.getElementById("galleryLoading");
  if (loading) {
    loading.classList.toggle("hidden", !show);
  }
}
